import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    Button,
    Space,
    Alert,
    Typography,
    Tag,
    Divider,
    Select,
} from '@app/ui';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';

type SocialBenchmarkingFavoritesProps = {
    open: boolean;
    onClose: () => void;
    onSaveChanges: (toAdd: number[], toRemove: number[]) => void;
    loading?: boolean;
    width?: number | string;
    maxSelectable?: number;
    leagues?: Array<{
        id: number;
        name: string;
        accountGroups: { id: number; name: string }[];
    }>;
    favoritesList?: {
        id: number;
        favorites: Array<{ id: number; leagueId: number }>;
    } | null;
};

const SocialBenchmarkingFavorites: React.FC<
    SocialBenchmarkingFavoritesProps
> = ({
    open,
    onClose,
    onSaveChanges,
    loading = false,
    width = '65%',
    maxSelectable = 30,
    leagues = [],
    favoritesList = null,
}) => {
    const { t } = useTranslation(['common', 'benchmarking']);
    const [currentFavoritesPerLeague, setCurrentFavoritesPerLeague] = useState<
        Record<number, Record<number, boolean>>
    >({});

    useEffect(() => {
        if (!favoritesList) return;
        const mapped = favoritesList.favorites?.reduce(
            (acc, { leagueId, id }) => {
                if (!acc[leagueId])
                    acc[leagueId] = {} as Record<number, boolean>;
                acc[leagueId][id] = true;
                return acc;
            },
            {} as Record<number, Record<number, boolean>>,
        );
        setCurrentFavoritesPerLeague(mapped || {});
    }, [favoritesList]);

    const computeSelectedCount = useMemo(() => {
        return Object.values(currentFavoritesPerLeague).reduce(
            (acc, favs) => acc + Object.values(favs).filter(Boolean).length,
            0,
        );
    }, [currentFavoritesPerLeague]);

    const getTruthyKeys = (obj: Record<number, boolean> = {}) =>
        Object.entries(obj)
            .filter(([, v]) => v)
            .map(([k]) => Number(k));

    const onSelectChange = (leagueId: number) => (values: number[]) => {
        const selectedMap = values.reduce(
            (acc, id) => {
                acc[id] = true;
                return acc;
            },
            {} as Record<number, boolean>,
        );
        setCurrentFavoritesPerLeague(prev => ({
            ...prev,
            [leagueId]: selectedMap,
        }));
    };

    const selectAllForLeague = (leagueId: number) => {
        const league = leagues.find(l => l.id === leagueId);
        const allIds = (league?.accountGroups || []).map(ag => ag.id);
        const selectedMap = allIds.reduce(
            (acc, id) => {
                acc[id] = true;
                return acc;
            },
            {} as Record<number, boolean>,
        );
        setCurrentFavoritesPerLeague(prev => ({
            ...prev,
            [leagueId]: selectedMap,
        }));
    };

    const sortedLeagues = useMemo(() => {
        return [...leagues].sort((a, b) => a.name.localeCompare(b.name));
    }, [leagues]);

    const [leftLeagues, rightLeagues] = useMemo(() => {
        const midpoint = Math.ceil(sortedLeagues.length / 2);
        return [
            sortedLeagues.slice(0, midpoint),
            sortedLeagues.slice(midpoint),
        ];
    }, [sortedLeagues]);

    const handleSave = () => {
        if (!favoritesList) return;
        const prevIds = favoritesList.favorites?.map(f => f.id) || [];
        const nowIds = Object.values(currentFavoritesPerLeague).flatMap(map =>
            getTruthyKeys(map),
        );
        const toAdd = nowIds.filter(id => !prevIds.includes(id));
        const toRemove = prevIds.filter(id => nowIds.indexOf(id) < 0);
        onSaveChanges(toAdd, toRemove);
    };

    return (
        <Drawer
            title={t('cancel', { ns: 'common' })}
            placement="right"
            width={width}
            onClose={onClose}
            open={open}
            zIndex={3001}
            extra={
                <Space>
                    <Button
                        onClick={() => {
                            setCurrentFavoritesPerLeague({});
                        }}
                        icon={<ReloadOutlined />}
                        style={{ background: '#1f1f1f', color: '#fff' }}
                    >
                        {t('socialBenchmarking.favorites.clearAll', {
                            ns: 'benchmarking',
                        })}
                    </Button>
                    <Button
                        type="primary"
                        loading={loading}
                        onClick={handleSave}
                        icon={<SaveOutlined />}
                    >
                        {t('buttons.saveChanges', { ns: 'common' })}
                    </Button>
                </Space>
            }
        >
            <Typography.Text
                strong
                style={{ fontSize: 14, display: 'block', marginBottom: 8 }}
            >
                {t('socialBenchmarking.favorites.manageTitle', {
                    ns: 'benchmarking',
                })}
            </Typography.Text>
            <Divider
                style={{ margin: '8px 0 12px', borderColor: '#0000000f' }}
            />
            <Alert
                type="info"
                showIcon
                message={t('socialBenchmarking.favorites.infoMessage', {
                    ns: 'benchmarking',
                })}
                style={{ marginBottom: 12 }}
            />
            <div style={{ marginTop: 12 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 8,
                    }}
                >
                    <Typography.Text strong style={{ fontSize: 14 }}>
                        {t('socialBenchmarking.favorites.configureTitle', {
                            ns: 'benchmarking',
                        })}
                    </Typography.Text>
                    <Tag
                        bordered
                        style={{
                            color: '#009FDF',
                            borderColor: '#009FDF',
                            backgroundColor: 'rgba(0, 159, 223, 0.12)',
                            borderWidth: 2,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>
                            {`${computeSelectedCount}/${maxSelectable}`}
                        </span>
                        <span>
                            {t('socialBenchmarking.favorites.selected', {
                                ns: 'benchmarking',
                            })}
                        </span>
                    </Tag>
                </div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(260px, 1fr))',
                        gap: 12,
                    }}
                >
                    <div>
                        {leftLeagues.map(l => {
                            const options = [...l.accountGroups]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(ag => ({ label: ag.name, value: ag.id }));
                            return (
                                <div key={l.id} style={{ marginBottom: 12 }}>
                                    <Typography.Text
                                        style={{
                                            display: 'block',
                                            marginBottom: 6,
                                        }}
                                    >
                                        {l.name}
                                    </Typography.Text>
                                    <Select
                                        mode="multiple"
                                        placeholder={t(
                                            'socialBenchmarking.favorites.selectPlaceholder',
                                            { ns: 'benchmarking' },
                                        )}
                                        style={{ width: '100%' }}
                                        options={options}
                                        value={getTruthyKeys(
                                            currentFavoritesPerLeague[l.id] ||
                                                {},
                                        )}
                                        onChange={onSelectChange(l.id)}
                                        optionFilterProp="label"
                                        allowClear
                                        maxTagCount={1}
                                        maxTagTextLength={10}
                                        size="middle"
                                        autoClearSearchValue={false}
                                        dropdownRender={menu => (
                                            <div>
                                                <div
                                                    onMouseDown={e =>
                                                        e.preventDefault()
                                                    }
                                                    onClick={() =>
                                                        selectAllForLeague(l.id)
                                                    }
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom:
                                                            '1px solid #f0f0f0',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t(
                                                        'socialBenchmarking.favorites.selectAll',
                                                        { ns: 'benchmarking' },
                                                    )}
                                                </div>
                                                {menu}
                                            </div>
                                        )}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div>
                        {rightLeagues.map(l => {
                            const options = [...l.accountGroups]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(ag => ({ label: ag.name, value: ag.id }));
                            return (
                                <div key={l.id} style={{ marginBottom: 12 }}>
                                    <Typography.Text
                                        style={{
                                            display: 'block',
                                            marginBottom: 6,
                                        }}
                                    >
                                        {l.name}
                                    </Typography.Text>
                                    <Select
                                        mode="multiple"
                                        placeholder={t(
                                            'socialBenchmarking.favorites.selectPlaceholder',
                                            { ns: 'benchmarking' },
                                        )}
                                        style={{ width: '100%' }}
                                        options={options}
                                        value={getTruthyKeys(
                                            currentFavoritesPerLeague[l.id] ||
                                                {},
                                        )}
                                        onChange={onSelectChange(l.id)}
                                        optionFilterProp="label"
                                        allowClear
                                        maxTagCount={1}
                                        maxTagTextLength={10}
                                        size="middle"
                                        autoClearSearchValue={false}
                                        dropdownRender={menu => (
                                            <div>
                                                <div
                                                    onMouseDown={e =>
                                                        e.preventDefault()
                                                    }
                                                    onClick={() =>
                                                        selectAllForLeague(l.id)
                                                    }
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom:
                                                            '1px solid #f0f0f0',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t(
                                                        'socialBenchmarking.favorites.selectAll',
                                                        { ns: 'benchmarking' },
                                                    )}
                                                </div>
                                                {menu}
                                            </div>
                                        )}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default SocialBenchmarkingFavorites;
